/**
 * The TemplateVars class encapsulates text variable handling
 * within a template's fields
 *
 * Template vars are represented in the custom property
 * brandly.variable, or (deprecated) textually, eg in the
 * visible text of a template.
 *
 * The custom property brandly.variable takes precedence
 * and is preferred because the sample preview (shown on dashboard)
 * doesn't have weird variable stuff on it.
 *
 * In the textual case they MUST be surrounded by brackets.
 * In the custom property, if brackets are present, then the
 * bracket notation is used.
 *
 * An example of variables in bracket notation:
 *
 * [ph1]
 *
 * That makes creation of a variable simple, if undiscoverable -- just put some
 * text in particular locations.
 *
 * You can specify sub-field formatting such as:
 * [ph1|000.000.0000]   -- phone formatting (ensure dots separate numbers)
 * [title|caps]         -- uppercase value
 *
 * Finally, when there is text preceding a variable, and the variable is blank
 * the text is also elided.
 * EG:
 *   "mobile [ph2]"
 * when ph2 is blank, the result is "" not "mobile "
 *   "[addr1], [addr2]"
 * when addr2 is blank, and addr1 is say "123 main", the result is "123 main"
 * In these and most cases the behavior is helpful.
 *
 * As an example of when the behavior doesn't help: when addr1 is blank and addr2
 * is say "suite 10", the result is ", suite 10"
 *
 * TODO explain/test sample values -- are prefixes ever really elided?
 * TODO explain images
 *
 * Images can also have variables. They must use a bracket notaion,
 * only QR generation is supported. Use [url|https|shorten|qr]
 * where "https" prepends https if missing, "shorten" makes a QR1.TO url,
 * and "qr" makes the qr code (must be last). "url" in this example is the
 * variable.
 *
 */
import QRCode from "qrcode";
import {shortenUrl, validUrl} from "@/services/shorturl"
export default class TemplateVars {
  constructor(fabricCanvas, store) {
    this.fabricCanvas = fabricCanvas;
    this.store = store;
    this.issues = [];
  }

  /**
   * Return an object with well-known variables for personal values,
   * such as first name, last name, phone, etc.
   */
  personalValues() {
    return this.store.getters.personalInfo;
  }

  /**
   * Return sample values. Using sample values is more
   * obvious when using the product.
   */
  sampleValues() {
    return {
      'name': 'First Last',
      'title': 'Title',
      'ph1': '000-000-0000',
      'ph2': '000-000-0000',
      'ph3': '000-000-0000',
      'addr1': 'Street Address',
      'addr2': 'City, ST 00000',
    }
  }

  missingVarValue(varname) {
    console.log('missingVarValue', varname);
    return this.sampleValues()[varname] || '';
  }

  /**
   * Update the fabric objects by replacing text.
   * Caller should call renderAll().
   */
  async replaceAll() {
    let vars = this.personalValues();
    return Promise.all(
      this.fabricCanvas.getObjects().map(
        object => this.replaceVars(object, vars)
      )
    )
  }

  async replaceVars(object, vars) {
    if (!object.brandly) object.brandly = {};
    let {variable, replaced, textTransform} = object.brandly;
    if (replaced) return;
    if (object.type == 'image') {
      if (variable) {
        let imageSpec = await this.replacementImageVars(variable, vars);
        await this.generateImage(object, imageSpec);
      }
    } else if (variable) {
      let [prefix, replacement] = this.replacementText(variable, vars, textTransform);
      this.replaceText(object, prefix, replacement);
    } else {
      // TODO deprecate
      this.replaceTextVars(object, vars);
    }
    object.brandly.replaced = true;
  }

  async generateImage(object, imageSpec) {
    if (imageSpec === 'remove:') {
      // Delete object
      this.fabricCanvas.remove(object);
      this.fabricCanvas.renderAll();
    } else if (imageSpec.startsWith('qr:')) {
      // Generate a QR code
      let dataUrl = await QRCode.toDataURL(imageSpec.substring(3), {scale:10});
      let origWidth = object.getScaledWidth();
      object.setSrc(dataUrl, img => {
        img.setCoords();
        img.scaleToWidth(origWidth);
        img.setCoords();
        console.log('setSrc', dataUrl);
        this.fabricCanvas.renderAll();
      });
      // Record the imageSpec for posterity
      object.brandly.imageSpec = imageSpec;
    } else {
      // Internal error.. we could remove..
      console.error('Bad imageSpec');
    }
  }

  replacementText(variable, vars, textTransform) {
    // Allow just a variable name to work as expected
    if (variable.match(/^[a-z]+\d*$/)) variable = '[' + variable + ']';
    let re = /(.*?)\[([a-z]+\d*)(\|.*?)?\]/g;
    let text = variable.replace(re, (...m) => this.replacement(m, vars));
    text = this.transformText(text, textTransform);

    // Now determine if the prefix (part of variable before "[")
    // matches the text, and if so, return seaparately.
    // This allows replaceText to intelligently keep the font of labels
    let m = re.exec(variable), prefix = m ? m[1] : '';
    if (text.startsWith(prefix)) {
      return [prefix, text.substring(prefix.length)];
    } else {
      return ['', text];
    }
  }

  replaceText(object, prefix, rest) {
    if (object.text.startsWith(prefix)) {
      let start = prefix.length;
      object.setSelectionStart(start);
      object.setSelectionEnd(start+1);
      const styles = object.getSelectionStyles();
      const styleArray = Array(rest.length).fill(styles[0]);
      // Replace the rest of the object text with proper style
      object.insertChars(rest, styleArray, start, object.text.length);
    } else {
      // Replace the whole object text
      object.text = prefix + rest;
    }
  }

  transformText(text, transform) {
    if (transform == 'uppercase') {
      text = text.toUpperCase();
    } else if (transform) {
      console.warn('Unknown transform', transform);
    }
    return text;
  }

  replaceTextVars(object, vars) {

    if (!object.text) return;

    let re = /(.*?)\[([a-z]+\d*)(\|.*?)?\]/g;
    let string = object.text;

    let match = re.exec(string);
    while (match){
      let [varSpec, prefix, varname, formatSpec] = match;
      let start = match.index + prefix.length;
      object.setSelectionStart(start);
      object.setSelectionEnd(start+1);
      const styles = object.getSelectionStyles();
      const replacement = this.replacement([varSpec, '', varname, formatSpec], vars)
      const styleArray = Array(replacement.length).fill(styles[0]);

      object.insertChars(replacement, styleArray, start, start + varSpec.length);
      string = object.text;
      match = re.exec(string);
    }
  }

  async replacementImageVars(variable, vars) {
    // Similar to replaceTextVars, but async (to allow for image generation, shortening etc
    // and using a different output: a tagged text.
    // So return 'qr:something' to make a QR code,
    // Or 'remove:' to remove the image,
    // Or in the future icon:something, etc.

    // Look for something like [var|https|shorten|qr]
    let match = /^\[([a-z]+\d*)\|([a-z|]+)\]$/.exec(variable)
    if (!match) {
      this.issues.push({issue: 'unknownImageVarFormat', variable});
      return 'remove:';
    }
    let varname = match[1];
    let formatcodes = match[2].split('|');

    // Get the variable value.
    let value = vars[varname];
    if (!value) return 'remove:';

    // Check pipeline ends with qr (the only working replacement)
    // We also don't need to dispatch the transformation, so just pop.
    if (formatcodes.pop() !== 'qr') {
      this.issues.push({issue: 'badFinalTransform', variable});
      return 'remove:';
    }

    // Transform according to pipeline.
    // If transform returns blank, remove.
    const transforms = {http, https, shorten};
    for (let code of formatcodes) {
      let transform = transforms[code] || unknownTransform;
      value = await transform(value);
    }

    return value ? 'qr:' + value : 'remove:';

    // Hoisted transforms
    async function http(value) {
      return !value.match(/^https?:/) ? 'http://' + value : value;
    }
    async function https(value) {
      return !value.match(/^https?:/) ? 'https://' + value : value;
    }
    async function shorten(value) {
      if (!validUrl(value)) {
        this.issues.push({issue: 'badUrlForShorten', value});
        // be strict in output.
        return '';
      }
      let url = await shortenUrl(value);
      return url.toUpperCase();
    }
    async function unknownTransform(value) {
      this.issues.push({issue: 'unknownTransform', variable});
      return 'remove:';
    }
  }

  replacement(match, vars) {
    let [varSpec, prefix, varname, formatSpec] = match;
    let value = vars[varname];
    if (value === null || value === undefined) {
      this.issues.push({issue: 'unknownVar', varname, varSpec, formatSpec});
    }
    if (!value) value = this.missingVarValue(varname);
    if (formatSpec) {
      // The primary use for formatting is to match phone format styles
      formatSpec = formatSpec.substring(1);
      let context = {
        issue: (issue, obj={}) => this.issues.push({...obj, issue, value, varname, varSpec, formatSpec})
      };
      for (let {pattern, format} of FORMATS) {
        let match = pattern.exec(formatSpec);
        if (match) {
          // Since the pattern matched, we will use the result
          // from the format func. The format func takes the
          // value and any groups from the match (no need to
          // pass the whole match).
          // The format func can signal an issue using this.issue('whatever')
          match[0] = value;
          value = format.apply(context, match);
          return value && (prefix + value);
        }
      }
      // No format found. Continue with value.
      this.issues.push({issue: 'unknownFormat', varname, varSpec, formatSpec});
    }
    return value && (prefix + value);
  }
}

const FORMATS = [
  {
    // Handle formats like: [ph1|000.000.0000]
    // Incoming values that also match US phone numbers
    // will be changed to use the separators in the format.
    pattern: /^\d{3}(\W)\d{3}(\W)\d{4}/,
    format(value, sep1, sep2) {
      let pieces = /^(\d{3})\W(\d{3})\W(\d{4})/.exec(value);
      if (!pieces) {
        this.issue("value isn't in xxx-xxx-xxxx format");
        return value;
      }
      return [pieces[1], sep1, pieces[2], sep2, pieces[3]].join('');
   }
  },
  {
    pattern: /^caps$/,
    format(value) {
      return value.toUpperCase();
    }
  }
]



// WEBPACK FOOTER //
// ./src/lib/TemplateVars.js